import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import { useOptic, useOpticDump } from "@/hooks/useOptic";
import { Loading } from "@/components/common/Loading";
import { Pathogen } from "@/components/common/Pathogen";
import { Tile, TileGrid, PreviewBanner } from "@/components/common/Tile";
import { SampleSet } from "@/api/qpcr";

const STD_CURVE_X = [6, 5, 4, 3, 2, 1, 0];

function pathogenLabel(s: SampleSet, idx: number): string {
  return s.pathogen_name ?? `pathogen ${idx + 1}`;
}

interface ControlPoint {
  cycle: number;
  "control +": number | null;
  "control NTC": number | null;
}

function ControlWellsChart({
  sampleSet,
  fallbackPositive,
  fallbackNtc,
}: {
  sampleSet: SampleSet;
  fallbackPositive?: (number | null)[];
  fallbackNtc?: (number | null)[];
}) {
  const data: ControlPoint[] = useMemo(() => {
    // Prefer the sample_set's own control arrays; otherwise fall back to
    // dump-derived arrays (best-effort: NTC=baseline, +ve=first cycle row).
    const pos =
      sampleSet.control_positive && sampleSet.control_positive.length > 0
        ? sampleSet.control_positive
        : fallbackPositive ?? [];
    const ntc =
      sampleSet.control_ntc && sampleSet.control_ntc.length > 0
        ? sampleSet.control_ntc
        : fallbackNtc ?? [];
    const len = Math.max(pos.length, ntc.length);
    const rows: ControlPoint[] = [];
    for (let c = 0; c < len; c++) {
      rows.push({
        cycle: c + 1,
        "control +": pos[c] ?? null,
        "control NTC": ntc[c] ?? null,
      });
    }
    return rows;
  }, [sampleSet, fallbackPositive, fallbackNtc]);

  if (data.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-textLight text-sm">
        No control well data to present.
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 24, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="cycle"
            stroke="#6B7280"
            fontSize={11}
            label={{ value: "Cycle Number", position: "insideBottom", offset: -8, fontSize: 11 }}
          />
          <YAxis
            stroke="#6B7280"
            fontSize={11}
            label={{ value: "RFU", angle: -90, position: "insideLeft", fontSize: 11 }}
          />
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line
            type="monotone"
            dataKey="control +"
            stroke="#059669"
            dot={false}
            isAnimationActive={false}
            strokeWidth={1.5}
          />
          <Line
            type="monotone"
            dataKey="control NTC"
            stroke="#DC2626"
            dot={false}
            isAnimationActive={false}
            strokeWidth={1.5}
            strokeDasharray="4 2"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

interface StdPoint { x: number; y: number }

function StandardCurveChart({ slope, intercept }: { slope: number; intercept: number }) {
  // Render flat placeholder line: y = slope*x + intercept across STD_CURVE_X.
  const data: StdPoint[] = STD_CURVE_X.map((x) => ({ x, y: slope * x + intercept }));
  return (
    <div className="flex-1 min-h-0">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 24, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="x"
            type="number"
            stroke="#6B7280"
            fontSize={11}
            domain={[0, 6]}
            label={{
              value: "Log Copies/mL",
              position: "insideBottom",
              offset: -8,
              fontSize: 11,
            }}
          />
          <YAxis
            stroke="#6B7280"
            fontSize={11}
            label={{ value: "Cq (Cycle Number)", angle: -90, position: "insideLeft", fontSize: 11 }}
          />
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Line
            type="linear"
            dataKey="y"
            stroke="#2563EB"
            dot={{ r: 3 }}
            isAnimationActive={false}
            strokeWidth={1.5}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function ResultDetails() {
  const { deviceId = "", opticId = "" } = useParams();
  const navigate = useNavigate();

  const opticQ = useOptic(opticId);
  const dumpQ = useOpticDump(opticId, !!opticQ.data?.has_dump);

  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    if (opticQ.data && selectedIdx >= (opticQ.data.sample_sets?.length ?? 0)) {
      setSelectedIdx(0);
    }
  }, [opticQ.data, selectedIdx]);

  if (opticQ.isLoading || !opticQ.data) return <Loading label="Loading audit…" />;
  if (opticQ.error) return <div className="text-danger-text text-sm">Failed to load audit.</div>;

  const optic = opticQ.data;
  const sampleSets = optic.sample_sets ?? [];
  const selected = sampleSets[selectedIdx];

  // Best-effort control-wells fallback from dump:
  //   - NTC line = dump.baseline
  //   - +ve control line = dump.cycles[0] (first cycle row)
  // NOTE: dump.cycles is per-well-per-cycle in row-major layout; this
  // fallback is approximate and only used when sample_set controls are empty.
  const dump = dumpQ.data;
  const fallbackNtc = dump?.baseline;
  const fallbackPositive = dump?.cycles?.[0];

  return (
    <div className="space-y-md">
      <div className="flex items-center justify-between">
        <Link
          to={`/devices/${deviceId}/results/${opticId}`}
          className="text-sm text-textLight hover:underline"
        >
          ← Back to result
        </Link>
        <div className="text-xs text-textLight font-mono">{optic.id}</div>
      </div>

      <PreviewBanner>
        Standard curve and Cq audit require a backend update — the standard-curve chart shows a
        placeholder line.
      </PreviewBanner>

      <TileGrid>
        <Tile
          title="Control Wells"
          className="col-span-12 lg:col-span-6 h-[400px]"
        >
          {selected ? (
            <ControlWellsChart
              sampleSet={selected}
              fallbackPositive={fallbackPositive}
              fallbackNtc={fallbackNtc}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-textLight text-sm">
              No target selected.
            </div>
          )}
        </Tile>

        <Tile
          title="Standard Curve"
          className="col-span-12 lg:col-span-4 h-[400px]"
        >
          <StandardCurveChart slope={-3.32} intercept={40} />
        </Tile>

        <Tile title="Targets" className="col-span-12 lg:col-span-2">
          <div className="flex flex-col gap-xs">
            <div className="text-xs text-textLight uppercase tracking-wide">Targets</div>
            {sampleSets.map((s, idx) => {
              const isSelected = idx === selectedIdx;
              return (
                <button
                  key={s.id ?? idx}
                  onClick={() => setSelectedIdx(idx)}
                  className={`text-left text-sm px-sm py-xs rounded-md ${
                    isSelected ? "bg-info-bg text-info-text" : "hover:bg-bg"
                  }`}
                >
                  <Pathogen name={pathogenLabel(s, idx)} />
                  {sampleSets.length === 1 ? " (Duplicated)" : ""}
                </button>
              );
            })}
            <div className="text-xs text-textLight uppercase tracking-wide mt-sm">Options</div>
            <button
              onClick={() => navigate("..")}
              className="text-left text-sm px-sm py-xs rounded-md hover:bg-bg"
            >
              Go Back
            </button>
            <button
              disabled
              title="Premium feature"
              className="text-left text-sm px-sm py-xs rounded-md opacity-50 cursor-not-allowed"
            >
              Reprocess
            </button>
          </div>
        </Tile>
      </TileGrid>
    </div>
  );
}
